import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewContainerRef,
  booleanAttribute,
  inject,
} from '@angular/core';
import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DsSidebarAwareDirective } from 'src/app/shared/directives/sidebar-aware/sidebar-aware.directive';

export type DsPopoverPlacement = 'top' | 'bottom' | 'left' | 'right';
export type DsPopoverTrigger = 'click' | 'hover';

const POSITION_MAP: Record<DsPopoverPlacement, ConnectedPosition> = {
  top: {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -8,
  },
  bottom: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -8,
  },
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 8,
  },
};

@Component({
  selector: 'ds-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [':host { display: inline-block; }'],
  template: `
    <ng-content />
  `,
})
export class DsPopoverComponent {
  @Input() content: TemplateRef<unknown> | null = null;
}

/**
 * Attaches a popover to a host element.
 *
 * Usage:
 *   <button [dsPopover]="contentRef" dsPopoverTrigger="click" dsPopoverPlacement="bottom">
 *     Open
 *   </button>
 *   <ng-template #contentRef>
 *     <div class="p-3">Rich content here</div>
 *   </ng-template>
 */
@Directive({
  selector: '[dsPopover]',
  standalone: true,
  exportAs: 'dsPopover',
  hostDirectives: [DsSidebarAwareDirective],
})
export class DsPopoverDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private positionBuilder = inject(OverlayPositionBuilder);
  private host = inject(ElementRef<HTMLElement>);
  private vcr = inject(ViewContainerRef);
  private sidebar = inject(DsSidebarAwareDirective);

  @Input({ required: true, alias: 'dsPopover' }) content!: TemplateRef<unknown>;
  @Input('dsPopoverPlacement') placement: DsPopoverPlacement = 'bottom';
  @Input('dsPopoverTrigger') trigger: DsPopoverTrigger = 'click';
  @Input({ alias: 'dsPopoverDisabled', transform: booleanAttribute }) disabled = false;
  // `dsPopoverOffset` đã GỠ 30/08/2026: khai từ đầu nhưng component không đọc ở đâu, và
  // 0 chỗ gọi truyền vào — tức `[dsPopoverOffset]="12"` sẽ không làm gì cả, im lặng.
  // Cùng lớp lỗi với `ds-input.clearable` (chết nhiều tháng). Khoảng cách hiện do
  // `POPOVER_PLACEMENTS` quyết định qua `offsetX`/`offsetY` của từng vị trí.
  // Cần cho chỉnh khoảng cách thì nối dây thật rồi mới khai lại input.

  @Output('dsPopoverOpened') openedChange = new EventEmitter<boolean>();

  private overlayRef?: OverlayRef;

  @HostListener('click')
  onHostClick(): void {
    if (this.trigger !== 'click' || this.disabled) return;
    this.toggle();
  }

  @HostListener('mouseenter')
  onHostEnter(): void {
    if (this.trigger !== 'hover' || this.disabled) return;
    this.open();
  }

  @HostListener('mouseleave')
  onHostLeave(): void {
    if (this.trigger !== 'hover') return;
    this.close();
  }

  toggle(): void {
    this.overlayRef?.hasAttached() ? this.close() : this.open();
  }

  open(): void {
    if (!this.content || this.disabled) return;
    if (!this.overlayRef) {
      const positionStrategy = this.positionBuilder
        .flexibleConnectedTo(this.host)
        .withPositions([POSITION_MAP[this.placement]])
        .withFlexibleDimensions(false)
        .withPush(false);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: this.trigger === 'click',
        backdropClass: 'cdk-overlay-transparent-backdrop',
        // Tag the panel with the current sidebar mode so CSS / consumers can
        // react to collapse/expand without subscribing to AppearanceService.
        panelClass: ['ds-popover-panel', `ds-sidebar-${this.sidebar.sidebarMode()}`],
      });
      this.overlayRef.backdropClick().subscribe(() => this.close());
    }
    if (this.overlayRef.hasAttached()) return;
    const portal = new TemplatePortal(this.content, this.vcr);
    this.overlayRef.attach(portal);
    this.openedChange.emit(true);
  }

  close(): void {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
      this.openedChange.emit(false);
    }
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }
}
